import { SearchResponse } from '../utils/types';
import { Organization } from './organization';
import { User } from './user';
import { LogTag } from './logTags';

/**
 * Represents an unparsed job log in the system.
 */
export interface JobLog {
  /** The display name for the job log. */
  description?: string;
  linesParsed: number;
  /**
   * @deprecated This field should not be relied on, its purpose has been overloaded a few different times over the years.
   * If you care about the value to display to the user, use {@link JobLog.description}. If you care about what the filename
   * was originally on the controller device, use {@link JobLog.originalFileName}.
   */
  name: string;
  org: string;
  physicalSerial: string;
  s3?: {
    Location: string;
    Bucket: string;
    Key: string;
    ETag?: string;
  };
  serialNumber: string;
  /** @deprecated use permissions instead. Check out the `usePolicy` hook to make it easy */
  share: any;
  /** @deprecated This is not reliable. Needs fixed on the backend. */
  timeStart: number;
  /** The name of the site the job occurred on. */
  siteName?: string;
  /** The model number of the heat exchanger the job was performed on. */
  heatExchangerModelNumber?: string;
  /** Time zone information stored to correctly resolve the job time. */
  deviceTimezone?: string;

  /** Milliseconds timestamp of the last update. */
  createdAt: number;
  /** The unique identifier of the user who created the log. */
  createdBy: string;
  /** Milliseconds timestamp of the last update. */
  updatedAt: number;
  /** The unique identifier of the user who created the log. */
  updatedBy: string;
  userEmail?: string;
  version: string;

  /** Guid unique identifier of the log. */
  _id: string;
  /** The job's status. If null or undefined, then 'complete'. */
  status?: 'complete' | 'in-progress' | null;
  permissions?: JobLogPermission[];
  /** The bundle associated with the job log. */
  bundleId?: string;
  /** The log file's original file name. */
  readonly originalFileName?: string;
  emailGuestShares: EmailGuestShare[] | undefined;
  logTagIds: string[];
  logTags: LogTag[];
}

/**
 * Represents the individual parts of the job report that can be shared/not shared.
 */
export interface JobReportPermissions {
  readAnnotations?: boolean;
  readTubes?: boolean;
  readPressures?: boolean;
  readPies?: boolean;
  readIdleVsPressure?: boolean;
  readConfiguration?: boolean;
  readTimestamps?: boolean;
}

export interface JobLogPermission {
  type: 'org';
  principal: string;
  principalName: string;
  expiresOn?: number;
  permissions: JobReportPermissions;
}

/**
 * Represents the share information that will be added to the emailGuestShares array field on a logs collection document.
 *
 * This is so different from the Permission interface, and it represents a share to a non-registered user.  For those
 * reasons, it is stored in a different field in a logs collection document.
 */
export interface EmailGuestShare {
  emailAddress: string;
  expiresOn: number;
  sharedBy: string;
  locale: string;
  permissions: JobReportPermissions;
}

/**
 * Represents incoming parameters to share a job report via email.
 *
 * jobId, emailAddress, givenName, familyName, and expiresOn are required
 * and will be checked for undefined/null. If any are not present
 * BadRequestError will be thrown, resulting in a 400 Bad Request response.
 *
 * The job must exist.  If not NotFoundError will be thrown, resulting
 * in a 404 Not Found response.
 *
 * All boolean values default to false if not present. Otherwise, the
 * given value will be used.
 *
 * The following MongoDB fields will be assigned values:
 *
 *   _id - will be assigned a newly-generated uuid.
 *   createdAt will be assigned the current time and date.
 *   createdBy will be assigned the id of the user in the request.
 */
export interface ShareViaEmailRequest {
  jobId: string;
  emailAddress: string;
  givenName: string;
  familyName: string;
  externalOrgName: string;
  expiresOn: number;
  locale: string;
  permissions: JobReportPermissions;
}

export interface JobLogDisplayPermissions {
  showTubeGraph: boolean;
  showAnnotations: boolean;
  showAnnotationUser: boolean;
  showAnnotationActions: boolean;
  showPieCharts: boolean;
  showIdleVsPressure: boolean;
  showPressures: boolean;
  showTimestamps: boolean;
  showEstimatedTimeToCompletion: boolean;
  showShareActions: boolean;
  showDeleteActions: boolean;
  showDownloadActions: boolean;
  showPrintActions: boolean;
}

export interface LogPrintConfig {
  // tube sheet variations
  showTubeSheetCompletion: boolean;
  showTubeSheetAutostrokes: boolean;
  showTubeSheetDepthCleaned: boolean;
  showTubeSheetLanceSpeed: boolean;
  showTubeSheetTime: boolean;

  // Tubes cleaned chart + annotations
  showTubesCleaned: boolean;
  showAnnotations: boolean;
  showAnnotationUser: boolean;

  // Other charts on info page
  showPieCharts: boolean;
  showIdleVsPressure: boolean;
  showPressures: boolean;

  // Additional configurations
  showTimestamps: boolean;
  showEstimatedTimeToCompletion: boolean;

  startTime?: number;
  endTime?: number;
}

export interface JobLogSearchRelations {
  organizations: Record<string, Organization>;
  users: Record<string, User>;
  physicalSerials: string[];
  deviceTimezones: string[];
  logTags: Record<string, LogTag>;
}

export const SHORT_EXPIRATION_IN_DAYS = 15;
export const MEDIUM_EXPIRATION_IN_DAYS = 30;
export const LONG_EXPIRATION_IN_DAYS = 45;

export interface LogSearchResult extends SearchResponse<JobLog, JobLogSearchRelations> {}
